import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  CircularProgress,
  Paper,
  Divider,
  Grid,
} from '@mui/material';
import {
  CheckCircle as RequiredIcon,
  Star as DesiredIcon,
  Cancel as ForbiddenIcon,
  Add as AddIcon,
  FiberManualRecord as BulletIcon,
} from '@mui/icons-material';
import { Skill } from '../../types';
import skillService from '../../services/skill.service';
import skillContainerService from '../../services/skillContainerService';
import { toast } from 'react-toastify';

interface SkillFilterSelectorProps {
  requiredSkills: Skill[];
  desiredSkills: Skill[];
  forbiddenSkills: Skill[];
  onRequiredSkillsChange: (skills: Skill[]) => void;
  onDesiredSkillsChange: (skills: Skill[]) => void;
  onForbiddenSkillsChange: (skills: Skill[]) => void;
}

type SkillCategory = 'required' | 'desired' | 'forbidden';

interface Container {
  id: number;
  title: string;
  parent: number | null;
  children?: Container[];
  skills?: Skill[];
}

const SkillFilterSelector: React.FC<SkillFilterSelectorProps> = ({
  requiredSkills,
  desiredSkills,
  forbiddenSkills,
  onRequiredSkillsChange,
  onDesiredSkillsChange,
  onForbiddenSkillsChange,
}) => {
  const [rootContainers, setRootContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<SkillCategory>('required');
  const [tempSelectedSkills, setTempSelectedSkills] = useState<Skill[]>([]);

  // Load all skill data in hierarchical structure
  const loadAllData = async () => {
    try {
      setLoading(true);

      // Load root containers (parent = null)
      const rootCont = await skillContainerService.getContainers(null);

      // For each root container, load subcontainers and skills
      const enrichedRoots = await Promise.all(
        rootCont.map(async (root: Container) => {
          // Load subcontainers
          const subcontainers = await skillContainerService.getContainers(root.id);

          // For each subcontainer, load its skills
          const enrichedSubs = await Promise.all(
            subcontainers.map(async (sub: Container) => {
              const skills = await skillService.getAll({ container: sub.id });
              return { ...sub, skills };
            })
          );

          // Load skills directly in root container (without subcontainer)
          const rootSkills = await skillService.getAll({ container: root.id });

          return {
            ...root,
            children: enrichedSubs,
            skills: rootSkills
          };
        })
      );

      setRootContainers(enrichedRoots);
    } catch (error) {
      console.error('Error loading skills data:', error);
      toast.error('Erreur lors du chargement des savoir-faire');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (category: SkillCategory) => {
    setCurrentCategory(category);
    // Set current selected skills for this category
    switch (category) {
      case 'required':
        setTempSelectedSkills([...requiredSkills]);
        break;
      case 'desired':
        setTempSelectedSkills([...desiredSkills]);
        break;
      case 'forbidden':
        setTempSelectedSkills([...forbiddenSkills]);
        break;
    }
    setModalOpen(true);

    // Load data if not already loaded
    if (rootContainers.length === 0) {
      await loadAllData();
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTempSelectedSkills([]);
  };

  const handleSaveSkills = () => {
    switch (currentCategory) {
      case 'required':
        onRequiredSkillsChange(tempSelectedSkills);
        break;
      case 'desired':
        onDesiredSkillsChange(tempSelectedSkills);
        break;
      case 'forbidden':
        onForbiddenSkillsChange(tempSelectedSkills);
        break;
    }
    handleCloseModal();
  };

  const handleToggleSkill = (skill: Skill) => {
    const isSelected = tempSelectedSkills.some(s => s.id === skill.id);
    if (isSelected) {
      setTempSelectedSkills(tempSelectedSkills.filter(s => s.id !== skill.id));
    } else {
      setTempSelectedSkills([...tempSelectedSkills, skill]);
    }
  };

  const handleRemoveSkill = (category: SkillCategory, skillId: number) => {
    switch (category) {
      case 'required':
        onRequiredSkillsChange(requiredSkills.filter(s => s.id !== skillId));
        break;
      case 'desired':
        onDesiredSkillsChange(desiredSkills.filter(s => s.id !== skillId));
        break;
      case 'forbidden':
        onForbiddenSkillsChange(forbiddenSkills.filter(s => s.id !== skillId));
        break;
    }
  };

  const handleDragStart = (e: React.DragEvent, skill: Skill, sourceCategory: SkillCategory) => {
    e.dataTransfer.setData('skill', JSON.stringify(skill));
    e.dataTransfer.setData('sourceCategory', sourceCategory);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetCategory: SkillCategory) => {
    e.preventDefault();

    const skillData = e.dataTransfer.getData('skill');
    const sourceCategory = e.dataTransfer.getData('sourceCategory') as SkillCategory;

    if (!skillData || sourceCategory === targetCategory) return;

    const skill: Skill = JSON.parse(skillData);

    // Remove from source category
    switch (sourceCategory) {
      case 'required':
        onRequiredSkillsChange(requiredSkills.filter(s => s.id !== skill.id));
        break;
      case 'desired':
        onDesiredSkillsChange(desiredSkills.filter(s => s.id !== skill.id));
        break;
      case 'forbidden':
        onForbiddenSkillsChange(forbiddenSkills.filter(s => s.id !== skill.id));
        break;
    }

    // Add to target category
    switch (targetCategory) {
      case 'required':
        onRequiredSkillsChange([...requiredSkills, skill]);
        break;
      case 'desired':
        onDesiredSkillsChange([...desiredSkills, skill]);
        break;
      case 'forbidden':
        onForbiddenSkillsChange([...forbiddenSkills, skill]);
        break;
    }
  };

  const renderSubcontainer = (subcontainer: Container) => (
    <Box key={subcontainer.id} sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {subcontainer.title}
      </Typography>

      <List dense>
        {subcontainer.skills && subcontainer.skills.length > 0 ? (
          subcontainer.skills.map((skill) => {
            const isSelected = tempSelectedSkills.some(s => s.id === skill.id);

            return (
              <ListItem
                key={skill.id}
                sx={{
                  py: 0.5,
                  px: 0,
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleToggleSkill(skill)}
                  size="small"
                />
                <BulletIcon sx={{ fontSize: 8, mr: 1 }} />
                <ListItemText
                  primary={skill.name}
                  primaryTypographyProps={{ variant: 'body2' }}
                  sx={{ my: 0, flex: 1 }}
                />
              </ListItem>
            );
          })
        ) : (
          <ListItem sx={{ py: 0.5, px: 0 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Aucun savoir-faire
            </Typography>
          </ListItem>
        )}
      </List>
    </Box>
  );

  const renderRootContainer = (container: Container) => (
    <Paper
      key={container.id}
      sx={{
        p: 3,
        height: '100%',
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {container.title}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {container.children && container.children.length > 0 ? (
          container.children.map(renderSubcontainer)
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Aucun sous-container
          </Typography>
        )}

        {/* Skills directly in root container */}
        {container.skills && container.skills.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Savoir-faire sans sous-container:
            </Typography>
            <List dense>
              {container.skills.map((skill) => {
                const isSelected = tempSelectedSkills.some(s => s.id === skill.id);

                return (
                  <ListItem
                    key={skill.id}
                    sx={{
                      py: 0.5,
                      px: 0,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleSkill(skill)}
                      size="small"
                    />
                    <BulletIcon sx={{ fontSize: 8, mr: 1 }} />
                    <ListItemText
                      primary={skill.name}
                      primaryTypographyProps={{ variant: 'body2' }}
                      sx={{ my: 0, flex: 1 }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Box>
    </Paper>
  );

  const getCategoryConfig = (category: SkillCategory) => {
    switch (category) {
      case 'required':
        return {
          label: 'Obligatoires',
          icon: <RequiredIcon sx={{ fontSize: 18 }} />,
          color: 'success' as const,
          skills: requiredSkills,
          description: 'Au moins un de ces savoir-faire doit être présent',
        };
      case 'desired':
        return {
          label: 'Souhaités',
          icon: <DesiredIcon sx={{ fontSize: 18 }} />,
          color: 'primary' as const,
          skills: desiredSkills,
          description: 'Plus il y en a, meilleur sera le score',
        };
      case 'forbidden':
        return {
          label: 'Interdits',
          icon: <ForbiddenIcon sx={{ fontSize: 18 }} />,
          color: 'error' as const,
          skills: forbiddenSkills,
          description: 'Aucune solution ne doit contenir ces savoir-faire',
        };
    }
  };

  const renderSkillBlock = (category: SkillCategory) => {
    const config = getCategoryConfig(category);

    return (
      <Box
        key={category}
        sx={{
          mb: 2,
          p: 2,
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: config.color === 'success' ? 'success.main' : config.color === 'error' ? 'error.main' : 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, category)}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            {config.icon}
            <Typography
              variant="body2"
              fontWeight={600}
              color={config.color === 'success' ? 'success.main' : config.color === 'error' ? 'error.main' : 'primary.main'}
            >
              {config.label} ({config.skills.length})
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal(category)}
            sx={{ minWidth: 'auto' }}
          >
            Ajouter
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {config.description}
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={0.5} minHeight={40}>
          {config.skills.length === 0 ? (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Glissez-déposez des savoir-faire ici
            </Typography>
          ) : (
            config.skills.map(skill => (
              <Chip
                key={skill.id}
                label={skill.name}
                size="small"
                color={config.color}
                variant="outlined"
                onDelete={() => handleRemoveSkill(category, skill.id)}
                draggable
                onDragStart={(e) => handleDragStart(e, skill, category)}
                sx={{
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' }
                }}
              />
            ))
          )}
        </Box>
      </Box>
    );
  };

  const currentConfig = getCategoryConfig(currentCategory);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Filtrer par savoir-faire
      </Typography>

      {renderSkillBlock('required')}
      {renderSkillBlock('desired')}
      {renderSkillBlock('forbidden')}

      {/* Skill Selection Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {currentConfig.icon}
            Sélectionner des savoir-faire {currentConfig.label.toLowerCase()}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {tempSelectedSkills.length} savoir-faire sélectionné{tempSelectedSkills.length > 1 ? 's' : ''}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {rootContainers.map((container) => (
                <Grid item xs={12} md={6} key={container.id}>
                  {renderRootContainer(container)}
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button onClick={handleSaveSkills} variant="contained" color={currentConfig.color}>
            Enregistrer ({tempSelectedSkills.length} savoir-faire)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SkillFilterSelector;
